import { ErrorState, LoadingState, StaleBanner } from '../../components/ui/AsyncState';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoveryQuery as useQuery } from '../../components/ui/useRecoveryQuery';
import { getAnalyticsOverview } from '../../api/endpoints/admin';
import { historyQueryPolicy } from '../../api/queryPolicies';
import { KpiCard } from '../../components/ui/KpiCard';
import { SectionCard } from '../../components/ui/SectionCard';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { useOpsUser } from '../ownership/opsUser';
import { OpsTeamScorecardTab } from '../scorecard/OpsTeamScorecardTab';
import { PaymentsMetricsTab } from './PaymentsMetricsTab';
import styles from './AnalyticsView.module.css';
import { DatePicker } from '../../components/ui/DatePicker';
import { KpiIcon } from '../../components/ui/KpiIcon';

const DEFAULT_WINDOW_DAYS = 7;

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** The date picker gives a bare yyyy-MM-dd — widen to an instant range for the API. */
function startOfDayIso(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00.000Z`).toISOString();
}

function endOfDayIso(dateStr: string): string {
  return new Date(`${dateStr}T23:59:59.999Z`).toISOString();
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

function hoursLabel(v: number | null): string {
  return v == null ? '—' : `${v.toFixed(1)}h`;
}

export function AnalyticsView() {
  const { t } = useTranslation();
  const { isManager } = useOpsUser();
  const [activeTab, setActiveTab] = useState<'overview' | 'ops-team' | 'payments'>('overview');

  const today = new Date();
  const defaultFrom = new Date(today.getTime() - DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000);
  const [fromDate, setFromDate] = useState(isoDate(defaultFrom));
  const [toDate, setToDate] = useState(isoDate(today));

  const from = startOfDayIso(fromDate);
  const to = endOfDayIso(toDate);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'analytics', 'overview', from, to],
    queryFn: () => getAnalyticsOverview({ from, to }),
    ...historyQueryPolicy,
    enabled: activeTab === 'overview',
  });

  return (
    <div className={styles.page}>
      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'payments'}
          className={`${styles.tabBtn} ${activeTab === 'payments' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          {t('analytics.analyticsView.tabPayments')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'overview'}
          className={`${styles.tabBtn} ${activeTab === 'overview' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          {t('analytics.analyticsView.tabOverview')}
        </button>
        {isManager && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'ops-team'}
            className={`${styles.tabBtn} ${activeTab === 'ops-team' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('ops-team')}
          >
            {t('analytics.analyticsView.tabOpsTeam')}
          </button>
        )}
      </div>

      {activeTab === 'payments' && <PaymentsMetricsTab />}
      {activeTab !== 'payments' && (
        <div className={styles.filterRow}>
          <label className={styles.filterLabel}>
            {t('analytics.analyticsView.from')}
            <DatePicker value={fromDate} max={toDate} onChange={setFromDate} />
          </label>
          <label className={styles.filterLabel}>
            {t('analytics.analyticsView.to')}
            <DatePicker value={toDate} min={fromDate} max={isoDate(today)} onChange={setToDate} />
          </label>
          <RefreshButton
            queryKeys={
              activeTab === 'overview' ? [['admin', 'analytics']] : [['admin', 'scorecard', 'team']]
            }
            label={t('analytics.analyticsView.refresh')}
          />
        </div>
      )}

      {activeTab === 'ops-team' && isManager && (
        <OpsTeamScorecardTab fromDate={fromDate} toDate={toDate} />
      )}

      {activeTab === 'overview' && (
        <>
          {isLoading && <LoadingState label={t('analytics.analyticsView.loading')} />}
          {isError &&
            (data ? (
              <StaleBanner onRetry={() => refetch()} />
            ) : (
              <ErrorState
                title={t('analytics.analyticsView.loadError')}
                onRetry={() => refetch()}
              />
            ))}

          {data && (
            <>
              <div className={styles.kpiRow}>
                <KpiCard
                  label={t('analytics.analyticsView.shipmentsPosted')}
                  value={data.shipmentsPosted}
                  icon={<KpiIcon name="inventory" />}
                />
                <KpiCard
                  label={t('analytics.analyticsView.awardRate')}
                  value={pct(data.awardRate)}
                  icon={<KpiIcon name="thumbUp" />}
                  accent="green"
                />
                <KpiCard
                  label={t('analytics.analyticsView.avgTimeToAward')}
                  value={hoursLabel(data.avgHoursToAward)}
                  icon={<KpiIcon name="timer" />}
                />
                <KpiCard
                  label={t('analytics.analyticsView.awardSla')}
                  value={pct(data.awardSlaComplianceRate)}
                  icon={<KpiIcon name="target" />}
                />
                <KpiCard
                  label={t('analytics.analyticsView.cancellationRate')}
                  value={pct(data.cancellationRate)}
                  icon={<KpiIcon name="block" />}
                />
                <KpiCard
                  label={t('analytics.analyticsView.exceptionRate')}
                  value={pct(data.exceptionRate)}
                  icon={<KpiIcon name="warning" />}
                  accent="red"
                />
              </div>

              <SectionCard title={t('analytics.analyticsView.byPickupState')}>
                {data.byRegion.length === 0 ? (
                  <p className={styles.empty}>{t('analytics.analyticsView.empty')}</p>
                ) : (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('analytics.analyticsView.colState')}</th>
                        <th>{t('analytics.analyticsView.colPosted')}</th>
                        <th>{t('analytics.analyticsView.colAwarded')}</th>
                        <th>{t('analytics.analyticsView.awardRate')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byRegion
                        .slice()
                        .sort((a, b) => b.shipmentsPosted - a.shipmentsPosted)
                        .map((r) => (
                          <tr key={r.state}>
                            <td>{r.state}</td>
                            <td>{r.shipmentsPosted}</td>
                            <td>{r.shipmentsAwarded}</td>
                            <td>{pct(r.awardRate)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </SectionCard>
            </>
          )}
        </>
      )}
    </div>
  );
}
